import { useCallback, useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { LockerRoomMember, ProspectBudgetPlan, ProspectActivity } from './RosterManager'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

const SPORT_PRESETS = [
  'basketball', 'football', 'track', 'wrestling', 'soccer', 'baseball', 'softball',
  'volleyball', 'tennis', 'swimming', 'golf', 'lacrosse', 'hockey', 'cheer', 'dance',
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'eligible', label: 'Eligible', color: 'bg-blue-100 text-blue-800' },
  { value: 'injured', label: 'Injured', color: 'bg-amber-100 text-amber-800' },
  { value: 'graduated', label: 'Graduated', color: 'bg-purple-100 text-purple-800' },
  { value: 'transferred', label: 'Transferred', color: 'bg-gray-200 text-gray-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-gray-200 text-gray-600' },
]

interface AthleticProfile {
  sports?: string[]
  position?: string
  height_in?: number | null
  weight_lbs?: number | null
  class_year?: number | null
  age_group?: string
  team?: string
  location?: string
}

interface RosterGroup {
  id: string
  name: string
  color: string | null
  member_count: number
}

interface AthleteSlideoutProps {
  member: LockerRoomMember
  budgetPlan?: ProspectBudgetPlan
  activity?: ProspectActivity
  groups: RosterGroup[]
  athleteGroupIds: string[]
  token: string
  onClose: () => void
  onSaved: () => void
  onStatusChange: (prospectId: number, status: string) => Promise<void>
  onGroupToggle: (groupId: string, prospectId: number, add: boolean) => Promise<void>
}

function formatCurrency(cents?: number | null) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format((cents as number) / 100)
}

function formatHeight(inches?: number | null) {
  if (!inches) return ''
  const ft = Math.floor(inches / 12)
  const rem = inches % 12
  return `${ft}'${rem}"`
}

export default function AthleteSlideout({
  member, budgetPlan, activity, groups, athleteGroupIds, token,
  onClose, onSaved, onStatusChange, onGroupToggle,
}: AthleteSlideoutProps) {
  const prospect = member.prospect
  const name = prospect?.name || prospect?.headline || `Prospect #${member.prospect_id}`

  const [profile, setProfile] = useState<AthleticProfile>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Sport input
  const [sportInput, setSportInput] = useState('')

  // Load athletic profile from prospect
  useEffect(() => {
    if (!prospect) return
    const ap = (prospect as any).athletic_profile
    if (ap && typeof ap === 'object') {
      setProfile(ap as AthleticProfile)
    }
  }, [prospect])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaved(false)
    try {
      await fetch(`${CORE_API}/api/v1/roster/prospect/${member.prospect_id}/athletic-profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile),
      })
      setSaved(true)
      onSaved()
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('Failed to save athletic profile', err)
    } finally {
      setSaving(false)
    }
  }, [profile, member.prospect_id, token, onSaved])

  const addSport = (sport: string) => {
    const s = sport.trim().toLowerCase()
    if (!s) return
    setProfile((p) => ({
      ...p,
      sports: [...new Set([...(p.sports || []), s])],
    }))
    setSportInput('')
  }

  const removeSport = (sport: string) => {
    setProfile((p) => ({
      ...p,
      sports: (p.sports || []).filter((s) => s !== sport),
    }))
  }

  const memberStatus = (member as any).status || (member.active === false ? 'inactive' : 'active')

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-4">
          <div className="flex items-center gap-3">
            {prospect?.avatar_url ? (
              <img src={prospect.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-200 text-sm font-bold text-stone-600">
                {(name[0] || '?').toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-base font-bold text-stone-900">{name}</h2>
              <p className="text-xs text-stone-500">#{member.prospect_id}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-stone-400 hover:text-stone-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Status */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-400">Status</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onStatusChange(member.prospect_id, opt.value)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    memberStatus === opt.value ? opt.color + ' ring-2 ring-stone-400' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sports */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-400">Sports</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(profile.sports || []).map((sport) => (
                <span key={sport} className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-800">
                  {sport}
                  <button type="button" onClick={() => removeSport(sport)} className="text-indigo-400 hover:text-indigo-600">&times;</button>
                </span>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                className="flex-1 rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={sportInput}
                onChange={(e) => setSportInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSport(sportInput); } }}
                placeholder="Add sport..."
                list="sport-presets"
              />
              <button type="button" onClick={() => addSport(sportInput)} className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white">Add</button>
            </div>
            <datalist id="sport-presets">
              {SPORT_PRESETS.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          {/* Core measurables */}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-stone-700">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Position</span>
              <input
                className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={profile.position || ''}
                onChange={(e) => setProfile((p) => ({ ...p, position: e.target.value }))}
                placeholder="Point Guard"
              />
            </label>
            <label className="text-sm text-stone-700">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Class Year</span>
              <input
                type="number"
                className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={profile.class_year || ''}
                onChange={(e) => setProfile((p) => ({ ...p, class_year: parseInt(e.target.value) || null }))}
                placeholder="2027"
              />
            </label>
            <label className="text-sm text-stone-700">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Height (in)</span>
              <input
                type="number"
                className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={profile.height_in || ''}
                onChange={(e) => setProfile((p) => ({ ...p, height_in: parseInt(e.target.value) || null }))}
                placeholder="72"
              />
              {profile.height_in ? <span className="mt-0.5 block text-xs text-stone-400">{formatHeight(profile.height_in)}</span> : null}
            </label>
            <label className="text-sm text-stone-700">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Weight (lbs)</span>
              <input
                type="number"
                className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={profile.weight_lbs || ''}
                onChange={(e) => setProfile((p) => ({ ...p, weight_lbs: parseInt(e.target.value) || null }))}
                placeholder="180"
              />
            </label>
          </div>

          {/* Team + Age Group + Location */}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-stone-700">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Team</span>
              <input
                className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={profile.team || ''}
                onChange={(e) => setProfile((p) => ({ ...p, team: e.target.value }))}
                placeholder="Varsity"
              />
            </label>
            <label className="text-sm text-stone-700">
              <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Age Group</span>
              <input
                className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
                value={profile.age_group || ''}
                onChange={(e) => setProfile((p) => ({ ...p, age_group: e.target.value }))}
                placeholder="U16"
              />
            </label>
          </div>
          <label className="block text-sm text-stone-700">
            <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Location</span>
            <input
              className="mt-1 w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm focus:border-stone-400 focus:outline-none"
              value={profile.location || ''}
              onChange={(e) => setProfile((p) => ({ ...p, location: e.target.value }))}
              placeholder="Houston, TX"
            />
          </label>

          {/* Groups */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-400">Groups</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {groups.map((g) => {
                const isMember = athleteGroupIds.includes(g.id)
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => onGroupToggle(g.id, member.prospect_id, !isMember)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      isMember
                        ? 'bg-stone-900 text-white'
                        : 'border border-stone-200 text-stone-500 hover:border-stone-400'
                    }`}
                    style={isMember && g.color ? { backgroundColor: g.color } : undefined}
                  >
                    {g.name}
                  </button>
                )
              })}
              {groups.length === 0 && <span className="text-xs text-stone-400">No groups created yet</span>}
            </div>
          </div>

          {/* Budget summary */}
          {budgetPlan && (
            <div className="rounded-xl bg-stone-50 p-3">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-400">Budget</label>
              <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-stone-500">Annual</p>
                  <p className="font-semibold text-stone-900">{formatCurrency(budgetPlan.planned_annual_allocation_cents)}</p>
                </div>
                <div>
                  <p className="text-stone-500">Monthly limit</p>
                  <p className="font-semibold text-stone-900">{formatCurrency(budgetPlan.soft_monthly_limit_cents || budgetPlan.monthly_limit_cents)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Activity */}
          {activity && (
            <div className="rounded-xl bg-stone-50 p-3">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-400">Activity</label>
              <div className="mt-1 text-sm text-stone-700">
                <p>{activity.monthlyCount} purchases this month · {activity.totalCount} total</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 px-5 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-stone-900 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {saved && <span className="text-sm text-emerald-600">Saved!</span>}
          <button type="button" onClick={onClose} className="ml-auto text-sm text-stone-500 hover:text-stone-700">Close</button>
        </div>
      </div>
    </>
  )
}
